import { supabase } from './supabase';

interface HealthLog {
  id: string;
  user_id: string;
  date: string;
  weight?: number;
  ate_well: boolean;
  ate_sweets: boolean;
  drank_water: boolean;
}

interface WorkoutSession {
  id: string;
  user_id: string;
  date: string;
  day_of_week: number;
  completed: boolean;
}

interface SelfCareSession {
  id: string;
  user_id: string;
  date: string;
  day_of_week: number;
  completed: boolean;
}

export const healthService = {
  async getHealthLog(userId: string, date: string) {
    const { data, error } = await supabase
      .from('health_logs')
      .select('*')
      .eq('user_id', userId)
      .eq('date', date)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data as HealthLog | null;
  },

  async updateHealthLog(userId: string, date: string, updates: Partial<HealthLog>) {
    const { data: existing } = await supabase
      .from('health_logs')
      .select('id')
      .eq('user_id', userId)
      .eq('date', date)
      .single();

    if (existing) {
      const { data, error } = await supabase
        .from('health_logs')
        .update(updates)
        .eq('id', existing.id)
        .select()
        .single();

      if (error) throw error;
      return data as HealthLog;
    } else {
      const { data, error } = await supabase
        .from('health_logs')
        .insert([{
          user_id: userId,
          date,
          ...updates
        }])
        .select()
        .single();

      if (error) throw error;
      return data as HealthLog;
    }
  },

  async getWorkoutSessions(userId: string, date: string) {
    const { data, error } = await supabase
      .from('workout_sessions')
      .select('*')
      .eq('user_id', userId)
      .eq('date', date);

    if (error) throw error;
    return data as WorkoutSession[];
  },

  async toggleWorkoutSession(userId: string, date: string, dayOfWeek: number) {
    const { data: existing } = await supabase
      .from('workout_sessions')
      .select('*')
      .eq('user_id', userId)
      .eq('date', date)
      .eq('day_of_week', dayOfWeek)
      .single();

    if (existing) {
      const { data, error } = await supabase
        .from('workout_sessions')
        .update({ completed: !existing.completed })
        .eq('id', existing.id)
        .select()
        .single();

      if (error) throw error;
      return data as WorkoutSession;
    } else {
      const { data, error } = await supabase
        .from('workout_sessions')
        .insert([{
          user_id: userId,
          date,
          day_of_week: dayOfWeek,
          completed: true
        }])
        .select()
        .single();

      if (error) throw error;
      return data as WorkoutSession;
    }
  },

  async getSelfCareSessions(userId: string, date: string) {
    const { data, error } = await supabase
      .from('self_care_sessions')
      .select('*')
      .eq('user_id', userId)
      .eq('date', date);

    if (error) throw error;
    return data as SelfCareSession[];
  },

  async toggleSelfCareSession(userId: string, date: string, dayOfWeek: number) {
    const { data: existing } = await supabase
      .from('self_care_sessions')
      .select('*')
      .eq('user_id', userId)
      .eq('date', date)
      .eq('day_of_week', dayOfWeek)
      .single();

    if (existing) {
      const { data, error } = await supabase
        .from('self_care_sessions')
        .update({ completed: !existing.completed })
        .eq('id', existing.id)
        .select()
        .single();

      if (error) throw error;
      return data as SelfCareSession;
    } else {
      const { data, error } = await supabase
        .from('self_care_sessions')
        .insert([{
          user_id: userId,
          date,
          day_of_week: dayOfWeek,
          completed: true
        }])
        .select()
        .single();

      if (error) throw error;
      return data as SelfCareSession;
    }
  }
};
